import React, { useState } from "react";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckmarkControl from "components/v2/Common/CheckmarkControl";
import { StyledLineItemCard } from "./styled";

function LineItemCard({
  product,
  imageSrc,
  title,
  variant,
  quantity,
  price,
  replacement,
  statusLabel,
  selected = false,
  configured = false,
  ineligible = false,
  errorHighlighted = false,
  requestCreated = false,
  showSelectIndicator = false,
  showTrackRequest = false,
  showEdit = false,
  showDelete = false,
  editAriaLabel = "Edit item",
  deleteAriaLabel = "Remove item",
  ineligibleMessages = [],
  actions,
  actionsClassName = "",
  children,
  primaryColor = "#2563eb",
  rounded = true,
  clickable = true,
  onClick,
  onEdit,
  onDelete,
  onTrackRequest,
}) {
  const [replacementDetailsOpen, setReplacementDetailsOpen] = useState(false);
  const showSidePrice = Boolean(
    price && (showSelectIndicator || requestCreated),
  );
  const className = [
    "line-item-card",
    selected ? "selected" : "",
    configured ? "configured" : "",
    ineligible ? "ineligible" : "",
    errorHighlighted ? "error-highlighted" : "",
    requestCreated ? "request-created" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const stopAndRun = (event, callback) => {
    event.stopPropagation();
    callback?.(event);
  };

  return (
    <StyledLineItemCard
      className={className}
      primaryColor={primaryColor}
      rounded={rounded}
      clickable={clickable}
      onClick={onClick}
    >
      <div className="line-item-card-main">
        <img
          src={imageSrc}
          alt={title || product?.product_title || ""}
          className="line-item-image"
          loading="eager"
        />
        <div className="line-item-details">
          <p className="line-item-title">{title || product?.product_title}</p>
          {variant ? <p className="line-item-variant">{variant}</p> : null}
          {(quantity !== undefined && quantity !== null) || price || showEdit ? (
            <div className="line-item-meta-row">
              {quantity !== undefined && quantity !== null ? (
                <p className="line-item-quantity">Qty: {quantity}</p>
              ) : (
                <span />
              )}
              <div className="line-item-price-row">
                {price && !showSidePrice ? (
                  <p className="line-item-price">{price}</p>
                ) : null}
                {showEdit ? (
                  <button
                    type="button"
                    className="configured-edit-button"
                    aria-label={editAriaLabel}
                    onClick={(event) => stopAndRun(event, onEdit)}
                  >
                    <BorderColorOutlinedIcon />
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
          {ineligibleMessages.length > 0 ? (
            <div className="line-item-ineligible-messages">
              {ineligibleMessages.map((message, index) => (
                <p key={`${message}-${index}`}>- {message}</p>
              ))}
            </div>
          ) : null}
        </div>
        {showDelete ? (
          <div className="configured-card-actions">
            <button
              type="button"
              className="configured-delete-button"
              aria-label={deleteAriaLabel}
              onClick={(event) => stopAndRun(event, onDelete)}
            >
              <DeleteOutlineIcon />
            </button>
          </div>
        ) : showSelectIndicator ? (
          <div className="line-item-side-summary line-item-selection-summary">
            <CheckmarkControl
              className="line-item-select-indicator"
              primaryColor={primaryColor}
              selected={selected}
              size={20}
            />
            {price ? <p className="line-item-price">{price}</p> : null}
          </div>
        ) : showSidePrice ? (
          <div className="line-item-side-summary line-item-price-summary">
            <p className="line-item-price">{price}</p>
          </div>
        ) : null}
        {statusLabel ? (
          <span className="configured-corner-tag">{statusLabel}</span>
        ) : null}
      </div>

      {showTrackRequest ? (
        <div className="track-request-action">
          <button
            type="button"
            className="track-request-button"
            onClick={(event) => stopAndRun(event, onTrackRequest)}
          >
            <span>Track Request</span>
            <ArrowOutwardIcon />
          </button>
        </div>
      ) : null}

      {replacement ? (
        <div
          className="line-item-actions replacement-actions"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="replacement-summary">
            <p className="replacement-summary-label">Replacing with</p>
            <button
              aria-expanded={replacementDetailsOpen}
              className={`replacement-summary-panel ${
                replacementDetailsOpen ? "open" : ""
              }`}
              onClick={() => setReplacementDetailsOpen((prev) => !prev)}
              type="button"
            >
              <img
                className="replacement-summary-image"
                src={replacement.imageSrc}
                alt={replacement.title || ""}
              />
              <div className="replacement-summary-copy">
                <p className="replacement-summary-title">
                  {replacement.title}
                  {!replacementDetailsOpen && replacement.variant
                    ? ` • ${replacement.variant}`
                    : ""}
                </p>
                <div className="replacement-summary-expanded-copy">
                  {replacement.variant ? (
                    <p className="replacement-detail-meta">
                      {replacement.variant}
                    </p>
                  ) : null}
                  {replacement.quantity ? (
                    <p className="replacement-detail-meta">
                      Qty: {replacement.quantity}
                    </p>
                  ) : null}
                  {replacement.price ? (
                    <p className="replacement-detail-price">
                      {replacement.price}
                    </p>
                  ) : null}
                  </div>
              </div>
              <KeyboardArrowDownIcon
                className={replacementDetailsOpen ? "open" : ""}
              />
            </button>
          </div>
        </div>
      ) : null}

      {actions ? (
        <div
          className={`line-item-actions ${actionsClassName}`}
          onClick={(event) => event.stopPropagation()}
        >
          {actions}
        </div>
      ) : null}

      {children}
    </StyledLineItemCard>
  );
}

export default LineItemCard;
